import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { getClaimById, updateClaimStatus, reviewClaim, recommendClaimForApproval, approveClaim, clearClaimMessage, clearSelectedClaim, selectSelectedClaim, selectClaimsLoading, selectClaimsError, selectClaimsErrorMessage, selectClaimsSuccess } from '../features/claims/claimSlice';
import apiClient from '../utils/apiClient';
import { selectCurrentUser, selectUserRoles, selectCanApproveClaims, selectCanProcessClaims } from '../features/auth/authSlice';
import { getDocumentsByClaimId, clearDocumentMessage, selectUploadSuccess } from '../features/documents/documentSlice';
import { SUBMITTED, APPROVED, REJECTED, UNDER_REVIEW, MISSING_DOCUMENTS, RECOMMENDED_FOR_APPROVAL } from '../constants/claimStatus';
import { ADMIN } from '../constants/userRoles';
import { BUSINESS_CLAIM_TYPES } from '../constants/businessClaimTypes';

// Import extracted components
import ClaimDetailHeader from '../components/ClaimDetail/ClaimDetailHeader';
import StatusUpdateModal from '../components/ClaimDetail/StatusUpdateModal';
import DocumentUploadModal from '../components/ClaimDetail/DocumentUploadModal';
import ClaimReviewModal from '../components/ClaimDetail/ClaimReviewModal';
import RecommendationModal from '../components/ClaimDetail/RecommendationModal';
import ApprovalModal from '../components/ClaimDetail/ApprovalModal';
import ClaimInformation from '../components/ClaimDetail/ClaimInformation';
import DeathClaimDetails from '../components/ClaimDetail/DeathClaimDetails';
import DisabilityClaimDetails from '../components/ClaimDetail/DisabilityClaimDetails';
import CriticalIllnessDetails from '../components/ClaimDetail/CriticalIllnessDetails';
import DocumentsSection from '../components/ClaimDetail/DocumentsSection';
import FinancialDetails from '../components/ClaimDetail/FinancialDetails';
import StatusHistory from '../components/ClaimDetail/StatusHistory';
import ActionsPanel from '../components/ClaimDetail/ActionsPanel';
import BackButton from '../components/ui/BackButton';
import { SlaStatusCard } from '../components/sla/SlaBadge';
import { useSlaAccess } from '../utils/slaAccessControl';

// Utility function for formatting currency
const _formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'GHS',
    minimumFractionDigits: 2,
  }).format(amount);
};

const ClaimDetail = () => {
  const { claimId } = useParams();
  const dispatch = useDispatch();
  
  // Validate claimId format (must be a valid string ID)
  const isValidClaimId = claimId && claimId.trim().length > 0;
  
  const claim = useSelector(selectSelectedClaim);
  const isLoading = useSelector(selectClaimsLoading);
  const isError = useSelector(selectClaimsError);
  const errorMessage = useSelector(selectClaimsErrorMessage);
  const isSuccess = useSelector(selectClaimsSuccess);
  const uploadSuccess = useSelector(selectUploadSuccess);
  const user = useSelector(selectCurrentUser);
  const userRoles = useSelector(selectUserRoles);
  const canApprove = useSelector(selectCanApproveClaims);
  const canProcess = useSelector(selectCanProcessClaims);
  const slaAccess = useSlaAccess(user);
  
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [reviewComments, setReviewComments] = useState('');
  const [recommendationNotes, setRecommendationNotes] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalDecision, setApprovalDecision] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);
  const [isRecommending, setIsRecommending] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isApprovingBenefit, setIsApprovingBenefit] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [documentListKey, setDocumentListKey] = useState(0);

  const handleRequestMissingDocs = async () => {
    try {
      await dispatch(updateClaimStatus({
        claimId: claim.id,
        status: 'MISSING_DOCUMENTS',
        comment: reviewComments || 'Additional documents required'
      })).unwrap();
    } catch (error) {
      console.error('Error requesting missing documents:', error);
    }
  };

  useEffect(() => {
    if (claimId && isValidClaimId) {
      // Clear any existing claim data when navigating to a new claim
      dispatch(clearSelectedClaim());
      dispatch(clearClaimMessage());
      dispatch(clearDocumentMessage());
      
      dispatch(getClaimById(claimId))
        .unwrap()
        .then((claim) => {
          // Fallback: if claim doesn't have documents, fetch them separately
          if (!Array.isArray(claim.documents) || claim.documents.length === 0) {
            dispatch(getDocumentsByClaimId(claimId));
          }
        })
        .catch((err) => {
          console.error('Error fetching claim or documents fallback:', err);
        });
        
      // Set up automatic refresh for SLA data every 5 minutes
      const slaRefreshInterval = setInterval(() => {
        if (claimId && isValidClaimId) {
          dispatch(getClaimById(claimId)); // Refresh claim data including SLA
        }
      }, 5 * 60 * 1000); // 5 minutes
      
      return () => clearInterval(slaRefreshInterval);
    }
  }, [dispatch, claimId, isValidClaimId]); // Removed isSuccess and claim?.status to prevent unnecessary refetches

  useEffect(() => {
    if (uploadSuccess) {
      setShowDocumentUpload(false);
      setDocumentListKey(prevKey => prevKey + 1);
      
      // Console log removed for production
      
      const timer = setTimeout(() => {
        dispatch(clearDocumentMessage());
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [uploadSuccess, dispatch, claim?.documents]);

  const handleOpenDocumentUpload = () => {
    setShowDocumentUpload(true);
  };

  const handleCloseDocumentUpload = () => {
    setShowDocumentUpload(false);
  };

  const handleDocumentUploadSuccess = () => {
    // The useEffect hook will handle the success state
  };

  // Legacy function - updated to return Om Design System compatible properties
  // Now returns badge properties instead of CSS classes for consistency
  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case SUBMITTED:
        return { variant: 'default', color: 'primary' };
      case UNDER_REVIEW:
        return { variant: 'default', color: 'warning' };
      case RECOMMENDED_FOR_APPROVAL:
        return { variant: 'default', color: 'secondary' };
      case APPROVED:
        return { variant: 'default', color: 'success' };
      case REJECTED:
        return { variant: 'default', color: 'danger' };
      case MISSING_DOCUMENTS:
        return { variant: 'dot', color: 'warning' };
      case 'PAYMENT_PENDING':
        return { variant: 'default', color: 'warning' };
      case 'PAID':
        return { variant: 'default', color: 'success' };
      case 'CLOSED':
        return { variant: 'default', color: 'neutral' };
      default:
        return { variant: 'default', color: 'neutral' };
    }
  };

  const getClaimTypeLabel = (type) => {
    // First try business claim types
    if (BUSINESS_CLAIM_TYPES[type]?.displayName) {
      return BUSINESS_CLAIM_TYPES[type].displayName;
    }
    
    // Fallback for enum values from backend
    const enumLabels = {
      'DEATH': 'Death Claim',
      'DISABILITY': 'Disability Claim', 
      'CRITICAL_ILLNESS': 'Critical Illness',
      'SPOUSAL_COVER': 'Spousal Cover',
      'COFFIN_COVER': 'Coffin Cover',
      'PARENT_COVER': 'Parent Cover',
      'MEDICAL': 'Medical Claim'
    };
    
    return enumLabels[type] || type || 'Unknown';
  };

  const canUpdateStatus = () => {
    const result = (() => {
      if (userRoles.includes('Admin')) return true;
      if (userRoles.includes('Claims_Officer')) return true; // Simplified - removed canProcess dependency
      if (userRoles.includes('Approver') && canApprove) return true;
      if (userRoles.includes('Finance') && claim?.status === 'APPROVED') return true;
      return false;
    })();
    
    console.log('canUpdateStatus check:', { 
      userRoles, 
      canApprove, 
      claimStatus: claim?.status, 
      result 
    });
    
    return result;
  };

  const getAvailableStatuses = () => {
    const currentStatus = claim?.status;
    const statuses = [];

    console.log('Getting available statuses for:', { currentStatus, userRoles });

    if (userRoles.includes('Claims_Officer')) {
      if (currentStatus === 'SUBMITTED') {
        statuses.push(
          { value: 'UNDER_REVIEW', label: 'Under Review' },
          { value: 'MISSING_DOCUMENTS', label: 'Pending Documents' },
          { value: 'REJECTED', label: 'Rejected' }
        );
      }
      if (currentStatus === 'UNDER_REVIEW') {
        statuses.push(
          { value: 'MISSING_DOCUMENTS', label: 'Pending Documents' },
          { value: 'REJECTED', label: 'Rejected' }
          // Note: Claims Officers use the "Recommend for Approval" button for RECOMMENDED_FOR_APPROVAL
        );
      }
      if (currentStatus === 'MISSING_DOCUMENTS') {
        statuses.push(
          { value: 'UNDER_REVIEW', label: 'Under Review' }
        );
      }
      if (currentStatus === 'VERIFICATION_FAILED') {
        statuses.push(
          { value: 'UNDER_REVIEW', label: 'Under Review' },
          { value: 'MISSING_DOCUMENTS', label: 'Pending Documents' }
        );
      }
    }

    if (userRoles.includes('Approver')) {
      if (currentStatus === 'RECOMMENDED_FOR_APPROVAL' || currentStatus === 'BENEFIT_PENDING_APPROVAL') {
        // Approvers use the "Make Decision" button for APPROVED/REJECTED
        statuses.push(
          { value: 'UNDER_REVIEW', label: 'Send Back for Review' }
        );
      }
    }

    if (userRoles.includes('Finance')) {
      if (currentStatus === 'APPROVED') {
        statuses.push(
          { value: 'PAYMENT_PENDING', label: 'Payment Pending' },
          { value: 'PAID', label: 'Paid' }
        );
      }
      if (currentStatus === 'PAYMENT_PENDING') {
        statuses.push(
          { value: 'PAID', label: 'Paid' },
          { value: 'APPROVED', label: 'Revert to Approved' }
        );
      }
    }

    if (userRoles.includes('Admin')) {
      // Admins can make most transitions, but should respect workflow
      const adminStatuses = [
        { value: 'SUBMITTED', label: 'Submitted' },
        { value: 'UNDER_REVIEW', label: 'Under Review' },
        { value: 'MISSING_DOCUMENTS', label: 'Pending Documents' },
        { value: 'RECOMMENDED_FOR_APPROVAL', label: 'Recommended for Approval' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'PAYMENT_PENDING', label: 'Payment Pending' },
        { value: 'PAID', label: 'Paid' },
        { value: 'CLOSED', label: 'Closed' }
      ];
      statuses.push(...adminStatuses.filter(s => s.value !== currentStatus));
    }

    console.log('Available statuses:', statuses);
    return statuses;
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      console.warn('No status selected for update');
      return;
    }

    console.log('Updating claim status:', { claimId: claim.id, status: newStatus, comment: statusComment });
    setIsUpdatingStatus(true);
    try {
      const result = await dispatch(updateClaimStatus({ 
        claimId: claim.id, 
        status: newStatus,
        comment: statusComment 
      })).unwrap();
      console.log('Status update successful:', result);
      setShowStatusUpdate(false);
      setNewStatus('');
      setStatusComment('');
    } catch (error) {
      console.error('Status update failed:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Handle claim review
  const handleReview = async () => {
    setIsReviewing(true);
    try {
      await dispatch(reviewClaim({ 
        claimId: claim.id, 
        comments: reviewComments 
      }));
      setShowReviewModal(false);
      setReviewComments('');
    } catch (error) {
      console.error('Error reviewing claim:', error);
    } finally {
      setIsReviewing(false);
    }
  };

  // Handle Claims Officer recommendation
  const handleRecommendation = async () => {
    setIsRecommending(true);
    try {
      await dispatch(recommendClaimForApproval({
        claimId: claim.id,
        recommendationNotes,
        additionalInfo: '' // Can be expanded later if needed
      })).unwrap();
      setShowRecommendModal(false);
      setRecommendationNotes('');
    } catch (error) {
      console.error('Error recommending claim:', error);
    } finally {
      setIsRecommending(false);
    }
  };

  // Handle Approver decision
  const handleApprovalDecision = async () => {
    setIsApproving(true);
    try {
      await dispatch(approveClaim({
        claimId: claim.id,
        decision: approvalDecision,
        approvalNotes,
        rejectionReason: approvalDecision === 'REJECT' ? rejectionReason : undefined
      })).unwrap();
      setShowApprovalModal(false);
      setApprovalNotes('');
      setRejectionReason('');
      setApprovalDecision('');
    } catch (error) {
      console.error('Error processing approval decision:', error);
    } finally {
      setIsApproving(false);
    }
  };

    // Handle Benefit Amount Approval
  const handleBenefitApproval = async (benefitData) => {
    setIsApprovingBenefit(true);
    try {
      // Use the apiClient for consistent API calls
      await apiClient.put(`claims/${benefitData.claimId}/approve-benefit`, {
        approvedBenefitAmount: benefitData.approvedBenefitAmount,
        justification: benefitData.justification
      });

      // Refresh the claim data to show updated benefit status
      await dispatch(getClaimById(claim.id));
    } catch (error) {
      console.error('Error approving benefit amount:', error);
      throw error; // Re-throw for modal error handling
    } finally {
      setIsApprovingBenefit(false);
    }
  };

  if (!isValidClaimId) {
    return (
      <div className="min-h-screen bg-silver-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-crystal-white border border-warning/20 p-8">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 bg-warning/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-deep-jungle">Invalid Claim ID</h3>
                <p className="text-steel max-w-md">
                  The claim ID provided is not valid. Please ensure you have the correct claim ID and try again.
                </p>
              </div>
              <Link
                to="/claims"
                className="btn-primary focus-visible"
              >
                Back to Claims
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-silver-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Modern loading skeleton */}
          <div className="animate-fade-in">
            {/* Header skeleton */}
            <div className="bg-crystal-white border border-silver-mist mb-8">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="space-y-3">
                    <div className="h-8 w-64 bg-silver-mist loading-skeleton"></div>
                    <div className="h-5 w-48 bg-silver-mist loading-skeleton"></div>
                  </div>
                  <div className="h-6 w-24 bg-silver-mist loading-skeleton"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="h-20 bg-silver-mist loading-skeleton"></div>
                  <div className="h-20 bg-silver-mist loading-skeleton"></div>
                  <div className="h-20 bg-silver-mist loading-skeleton"></div>
                </div>
              </div>
            </div>
            
            {/* Content skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-crystal-white border border-silver-mist p-6">
                    <div className="h-6 w-48 bg-silver-mist loading-skeleton mb-4"></div>
                    <div className="space-y-3">
                      <div className="h-4 w-full bg-silver-mist loading-skeleton"></div>
                      <div className="h-4 w-3/4 bg-silver-mist loading-skeleton"></div>
                      <div className="h-4 w-1/2 bg-silver-mist loading-skeleton"></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-6">
                {[1, 2].map(i => (
                  <div key={i} className="bg-crystal-white border border-silver-mist p-6">
                    <div className="h-6 w-32 bg-silver-mist loading-skeleton mb-4"></div>
                    <div className="space-y-3">
                      <div className="h-4 w-full bg-silver-mist loading-skeleton"></div>
                      <div className="h-4 w-2/3 bg-silver-mist loading-skeleton"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-carbon-black bg-opacity-60">
        <div className="flex items-center justify-center min-h-screen p-4">
          {/* Background overlay */}
          <div className="fixed inset-0" aria-hidden="true" />

          {/* Modal panel */}
          <div className="relative top-12 mx-auto p-0 bg-crystal-white w-full max-w-2xl overflow-hidden shadow-depth border border-steel/20 rounded-lg">
            <div className="card-header">
              <h3 className="text-lg font-display font-semibold text-crystal-white">
                Error Loading Claim
              </h3>
            </div>

            <div className="card-body space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-error/10 flex items-center justify-center rounded-full">
                  <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-graphite">
                    {errorMessage || 'An error occurred while loading the claim details.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-crystal-white border-t border-silver-mist px-8 py-6">
              <div className="flex justify-end space-x-3">
                <omk-button button-type="outlined-button" button-size="large" leading-icon="arrow_back" onClick={() => (window.location.href = '/claims')}>
                  Back to Claims
                </omk-button>
                <omk-button button-type="filled-button" button-size="large" leading-icon="refresh" onClick={() => dispatch(getClaimById(claimId))}>
                  Retry
                </omk-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Only show "Claim Not Found" if we're not loading and there's no error
  if (!claim && !isLoading && !isError) {
    return (
      <div className="min-h-screen bg-silver-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-crystal-white border border-silver-mist p-8">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 bg-steel/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-deep-jungle">Claim Not Found</h3>
                <p className="text-steel max-w-md">
                  The claim you are looking for does not exist or you do not have permission to view it.
                </p>
              </div>
              <Link
                to="/claims"
                className="btn-primary focus-visible"
              >
                Back to Claims
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-silver-mist">
      <div className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-6 sm:mb-8">
          <BackButton />
        </div>
        
        <ClaimDetailHeader
          claim={claim}
          getClaimTypeLabel={getClaimTypeLabel}
          getStatusBadgeColor={getStatusBadgeColor}
          canUpdateStatus={canUpdateStatus}
          getAvailableStatuses={getAvailableStatuses}
          userRoles={userRoles}
          onStatusUpdate={() => setShowStatusUpdate(true)}
          onDocumentUpload={handleOpenDocumentUpload}
        />

        {/* Full-width SLA Monitor Section */}
        {slaAccess.shouldShowBadges && claim.sla && (
          <div className="mb-8">
            <SlaStatusCard slaData={claim.sla} claimType={claim.type} />
          </div>
        )}

        <StatusUpdateModal
          show={showStatusUpdate}
          onClose={() => setShowStatusUpdate(false)}
          newStatus={newStatus}
          setNewStatus={setNewStatus}
          statusComment={statusComment}
          setStatusComment={setStatusComment}
          getAvailableStatuses={getAvailableStatuses}
          onUpdate={handleStatusUpdate}
          isUpdating={isUpdatingStatus}
        />

        <DocumentUploadModal
          show={showDocumentUpload}
          onClose={handleCloseDocumentUpload}
          claim={claim}
          onUploadSuccess={handleDocumentUploadSuccess}
        />

        <ClaimReviewModal
          show={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          comments={reviewComments}
          setComments={setReviewComments}
          onReview={handleReview}
          isReviewing={isReviewing}
        />

        <RecommendationModal
          show={showRecommendModal}
          onClose={() => setShowRecommendModal(false)}
          onRecommend={handleRecommendation}
          isRecommending={isRecommending}
          recommendationNotes={recommendationNotes}
          setRecommendationNotes={setRecommendationNotes}
          claim={claim}
          onBenefitApprove={handleBenefitApproval}
          isApprovingBenefit={isApprovingBenefit}
        />

        <ApprovalModal
          show={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          onApprove={handleApprovalDecision}
          isApproving={isApproving}
          approvalDecision={approvalDecision}
          setApprovalDecision={setApprovalDecision}
          approvalNotes={approvalNotes}
          setApprovalNotes={setApprovalNotes}
          rejectionReason={rejectionReason}
          setRejectionReason={setRejectionReason}
          claim={claim}
        />

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
          <div className="xl:col-span-2">
            <div className="space-y-6 sm:space-y-8">
              <ClaimInformation claim={claim} getClaimTypeLabel={getClaimTypeLabel} />

              {claim.type === 'DEATH' && <DeathClaimDetails claim={claim} />}
              {claim.type === 'DISABILITY' && <DisabilityClaimDetails claim={claim} />}
              {claim.type === 'CRITICAL_ILLNESS' && <CriticalIllnessDetails claim={claim} />}

              <DocumentsSection
                claim={claim}
                userRoles={userRoles}
                documentListKey={documentListKey}
                onDocumentUpload={handleOpenDocumentUpload}
              />
            </div>
          </div>

          <div className="xl:col-span-1">
            <div className="space-y-6 sm:space-y-8">
              <FinancialDetails claim={claim} />
              <StatusHistory claim={claim} />
              <ActionsPanel
                userRoles={userRoles}
                onDocumentUpload={handleOpenDocumentUpload}
                claim={claim}
                onRecommend={() => setShowRecommendModal(true)}
                onApprove={() => setShowApprovalModal(true)}
                onReview={() => setShowReviewModal(true)}
                onRequestMissingDocs={handleRequestMissingDocs}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimDetail;
